/**
 * Card rating (toolkit `card-rating` parity): one resolver for the explorer
 * card and the map popup, so the two surfaces apply the same threshold and
 * the same placement to the same record.
 *
 * Data: the listing record already carries `rating: { average, count }`
 * (widgets/explorer/data.ts `ExploreProperty.rating`, common/property-details.ts
 * `PropertyDetailsRaw.rating`). No request is added.
 *
 * Settings (all optional, never materialized, so a stored widget with none of
 * them renders exactly as before):
 *   card.showRating        the existing switch; off = no rating anywhere
 *   card.ratingMinReviews  homes with fewer reviews show nothing (1..100).
 *                          Unset = any review count above 0, the behavior the
 *                          card had before this leaf. The toolkit module
 *                          defaults to 3; the platform keeps 1 so saved
 *                          widgets do not change.
 *   card.ratingPlacement   'title' (the title row on the card, under the
 *                          title in the popup) or 'location' (after the
 *                          location line). Unset = 'title'.
 */
export const RATING_PLACEMENTS = ['title', 'location'] as const
export type CardRatingPlacement = (typeof RATING_PLACEMENTS)[number]
export const RATING_MIN_REVIEWS_MAX = 100

/** The structural subset of `card` this reads (an explorer card or any caller). */
export type CardRatingSettings = {
  showRating?: unknown
  ratingMinReviews?: unknown
  ratingPlacement?: unknown
}

export type ResolvedCardRating = {
  /** Average to one decimal, e.g. "4.8". */
  average: string
  count: number
  placement: CardRatingPlacement
  /** Visible-count tooltip, same wording the card always used. */
  title: string
  /** Announced line (toolkit tk#1103 wording); set only when a new leaf is configured. */
  ariaLabel?: string
}

/**
 * The rating line for one home, or null when nothing should show. Pure.
 */
export function resolveCardRating(
  rating: { average: number; count: number } | null | undefined,
  card: CardRatingSettings | null | undefined,
): ResolvedCardRating | null {
  if (!card || card.showRating !== true || !rating) return null
  // Malformed records (null/NaN average, fractional or non-finite count) show
  // nothing instead of throwing in toFixed or printing "NaN".
  if (typeof rating.average !== 'number' || !Number.isFinite(rating.average)) return null
  if (typeof rating.count !== 'number' || !Number.isInteger(rating.count)) return null
  const min = typeof card.ratingMinReviews === 'number' && Number.isInteger(card.ratingMinReviews)
    ? Math.min(RATING_MIN_REVIEWS_MAX, Math.max(1, card.ratingMinReviews))
    : undefined
  if (!(rating.count > 0) || rating.count < (min ?? 1)) return null
  const placement: CardRatingPlacement = card.ratingPlacement === 'location' ? 'location' : 'title'
  const average = rating.average.toFixed(1)
  const configured = min !== undefined || card.ratingPlacement !== undefined
  return {
    average,
    count: rating.count,
    placement,
    title: `${rating.count} reviews`,
    ariaLabel: configured ? `Rated ${average} out of 5 from ${rating.count} ${rating.count === 1 ? 'review' : 'reviews'}` : undefined,
  }
}
